import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  X,
  Loader2,
  Wand2,
  Download,
  ImageIcon,
  AlertCircle,
  FolderHeart,
  Paintbrush,
  UserCircle,
  Hammer,
  ZoomIn,
  Camera,
  Sparkles as SparklesIcon,
  ChevronDown,
  Check,
  History,
} from 'lucide-react';
import { api, type ModelConfig } from '../../services/api';
import { photoRestoreAPI, type PhotoRestorePreset } from './api';
import { usePlatformSession } from '../../platform/session';
import { withAuthToken } from '../../utils/storageUrl';
import { AppShell } from '../../platform/AppShell';
import { QUALITY_OPTIONS } from '../../baselines/image/constants';

interface RestoreTask {
  id: string;
  originalUrl: string;
  resultUrl?: string;
  status: 'processing' | 'completed' | 'failed';
  presetId: string;
  createdAt: Date;
  errorMessage?: string | null;
}

/** 预设图标为前端 UI 资产；预设内容（label/description/prompt）由服务端下发 */
const PRESET_ICONS: Record<string, typeof SparklesIcon> = {
  general: SparklesIcon,
  colorize: Paintbrush,
  portrait: UserCircle,
  severe: Hammer,
  enhance: ZoomIn,
  vintage: Camera,
};

const FALLBACK_PRESET_ICON = SparklesIcon;

function getPresetIcon(presetId: string): typeof SparklesIcon {
  return PRESET_ICONS[presetId] ?? FALLBACK_PRESET_ICON;
}

export default function PhotoRestorePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState('general');
  const [quality, setQuality] = useState('medium');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentTask, setCurrentTask] = useState<RestoreTask | null>(null);
  const [error, setError] = useState('');
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [selectedModel, setSelectedModel] = useState('gpt-image-2');
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [presets, setPresets] = useState<PhotoRestorePreset[]>([]);
  const [presetsError, setPresetsError] = useState(false);

  const { isLoggedIn } = usePlatformSession();

  useEffect(() => {
    api.getModels().then((res) => {
      setModels(res.data);
      if (res.data.length > 0) {
        setSelectedModel(res.data[0].id);
      }
    }).catch(() => {});
  }, []);

  // 预设由照片修复应用服务端下发（调整预设无需发版前端）
  useEffect(() => {
    photoRestoreAPI.getPresets().then((res) => {
      setPresets(res.data);
      setPresetsError(false);
    }).catch(() => {
      setPresetsError(true);
    });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setSelectedImage(event.target.result as string);
        setError('');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRestore = async () => {
    if (!selectedImage) {
      setError('请先上传需要修复的照片');
      return;
    }

    if (!isLoggedIn) {
      setError('请先登录后再使用修复功能');
      return;
    }

    if (!presets.some((p) => p.id === selectedPreset)) {
      setError('修复预设加载中或不可用，请稍后重试');
      return;
    }

    const qualityOption = QUALITY_OPTIONS.find((q) => q.value === quality);

    setIsProcessing(true);
    setError('');

    try {
      // 应用只提交 presetId，prompt 由照片修复服务端按预设生成
      const response = await photoRestoreAPI.generate({
        presetId: selectedPreset,
        quality: qualityOption?.apiValue || 'medium',
        referenceImage: selectedImage,
        model: selectedModel,
      });

      const newTask: RestoreTask = {
        id: response.jobId,
        originalUrl: selectedImage,
        status: 'processing',
        presetId: selectedPreset,
        createdAt: new Date(),
      };

      setCurrentTask(newTask);

      api.pollJobStatus(
        response.jobId,
        (job) => {
          if (job.status === 'completed') {
            const url = job.resultUrl || '';

            setCurrentTask((prev) =>
              prev?.id === job.id ? { ...prev, status: 'completed', resultUrl: url } : prev
            );
          } else if (job.status === 'failed') {
            setCurrentTask((prev) =>
              prev?.id === job.id
                ? { ...prev, status: 'failed', errorMessage: job.errorMessage }
                : prev
            );
          }
        }
      ).catch(() => {
        setCurrentTask((prev) =>
          prev?.id === response.jobId ? { ...prev, status: 'failed' } : prev
        );
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '修复失败';
      setError(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async (url?: string) => {
    if (!url) return;
    try {
      // 私有媒体网关 URL 必须带短时令牌，否则 401
      const response = await fetch(withAuthToken(url));
      const blob = await response.blob();

      // 创建 blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // 创建下载链接
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `restored-photo-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 释放 blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('下载失败:', error);
      // 降级方案：直接打开图片（同样需要令牌）
      window.open(withAuthToken(url), '_blank');
    }
  };

  return (
    <AppShell
      appIcon={<History className="h-5 w-5" />}
      appName="照片修复"
      headerRight={
        <button
          onClick={() => navigate('/works')}
          className="btn-secondary text-sm flex items-center gap-1.5"
        >
          <FolderHeart className="w-4 h-4" />
          作品库
        </button>
      }
    >
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Hero */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-light-text mb-2">AI 照片修复</h1>
          <p className="text-light-secondary max-w-xl mx-auto">
            利用 AI 技术智能修复老旧、破损、褪色的照片，让珍贵回忆重现光彩
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Mode Selection - Horizontal */}
        <div className="card p-4 mb-6">
          <label className="text-sm font-medium text-light-text mb-3 block">修复模式</label>
          {presetsError ? (
            <p className="text-sm text-light-muted py-4 text-center">修复预设加载失败，请刷新重试</p>
          ) : presets.length === 0 ? (
            <p className="text-sm text-light-muted py-4 text-center">正在加载修复预设...</p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
              {presets.map((p) => {
                const Icon = getPresetIcon(p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPreset(p.id)}
                    className={`flex-shrink-0 flex flex-col items-center gap-2 p-3 rounded-xl border transition-all min-w-[100px] ${
                      selectedPreset === p.id
                        ? 'border-accent-blue bg-accent-blue/10 text-accent-blue'
                        : 'border-light-border hover:border-accent-blue/50 hover:bg-light-hover'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <div className="text-center">
                      <div className="text-sm font-medium whitespace-nowrap">{p.label}</div>
                      <div className="text-[10px] opacity-60 mt-0.5 whitespace-nowrap">{p.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-[1fr,280px] gap-6">
          {/* Left: Image Area */}
          <div className="card p-6">
            {!selectedImage ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-light-border hover:border-accent-blue rounded-2xl p-12 text-center cursor-pointer transition-all hover:shadow-md"
              >
                <div className="w-16 h-16 bg-light-hover rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-8 h-8 text-light-muted" />
                </div>
                <h3 className="text-lg font-medium text-light-text mb-1">上传老照片</h3>
                <p className="text-sm text-light-muted mb-4">
                  支持 JPG、PNG 格式，建议上传清晰度较高的原图
                </p>
                <button className="btn-primary text-sm">选择照片</button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-light-text">照片预览</h3>
                  <button
                    onClick={() => {
                      setSelectedImage(null);
                      setCurrentTask(null);
                    }}
                    className="p-1.5 text-light-muted hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Original */}
                  <div className="space-y-2">
                    <span className="text-xs text-light-muted">原始照片</span>
                    <div className="aspect-square bg-light-hover rounded-xl overflow-hidden border border-light-border">
                      <img
                        src={selectedImage}
                        alt="Original"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  {/* Result */}
                  <div className="space-y-2">
                    <span className="text-xs text-light-muted">修复结果</span>
                    <div className="aspect-square bg-light-hover rounded-xl overflow-hidden border border-light-border flex items-center justify-center">
                      {currentTask?.status === 'processing' ? (
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto mb-2" />
                          <span className="text-sm text-light-muted">修复中...</span>
                        </div>
                      ) : currentTask?.status === 'completed' && currentTask.resultUrl ? (
                        <img
                          src={withAuthToken(currentTask.resultUrl)}
                          alt="Restored"
                          className="w-full h-full object-contain"
                        />
                      ) : currentTask?.status === 'failed' ? (
                        <div className="text-center px-4">
                          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                          <span className="text-sm text-red-500">修复失败</span>
                          {currentTask.errorMessage && (
                            <p className="text-xs text-light-muted mt-1">{currentTask.errorMessage}</p>
                          )}
                        </div>
                      ) : (
                        <div className="text-center px-4">
                          <ImageIcon className="w-8 h-8 text-light-muted mx-auto mb-2" />
                          <span className="text-sm text-light-muted">等待修复</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {currentTask?.status === 'completed' && currentTask.resultUrl && (
                  <button
                    onClick={() => handleDownload(currentTask.resultUrl)}
                    className="w-full btn-primary py-2.5 flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    下载修复结果
                  </button>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Right: Settings */}
          <div className="space-y-4">
            {/* Model Selector */}
            <div className="card p-5">
              <label className="text-sm font-medium text-light-text mb-3 block">图片模型</label>
              <div className="relative">
                <button
                  onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                  className="w-full flex items-center justify-between bg-white border border-light-border rounded-xl px-4 py-3 hover:border-accent-blue transition-colors shadow-sm"
                >
                  <span className="text-light-text font-medium text-sm">
                    {models.find(m => m.id === selectedModel)?.name || '选择模型'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-light-muted transition-transform ${modelDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {modelDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-light-border rounded-xl shadow-xl z-20 py-1 max-h-60 overflow-auto">
                    {models.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => { setSelectedModel(model.id); setModelDropdownOpen(false); }}
                        className="w-full px-4 py-3 text-left hover:bg-light-hover flex items-center justify-between"
                      >
                        <div>
                          <span className="text-light-text font-medium text-sm">{model.name}</span>
                          {model.description && (
                            <p className="text-xs text-light-muted mt-0.5">{model.description}</p>
                          )}
                        </div>
                        {selectedModel === model.id && <Check className="w-4 h-4 text-accent-blue" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Quality Selector */}
            <div className="card p-5">
              <label className="text-sm font-medium text-light-text mb-3 block">画质选择</label>
              <div className="space-y-2">
                {QUALITY_OPTIONS.map((q) => {
                  const modelCredits = models.find(m => m.id === selectedModel)?.credits[q.value as keyof ModelConfig['credits']];
                  const displayCredits = modelCredits ?? q.credits;
                  return (
                    <button
                      key={q.value}
                      onClick={() => setQuality(q.value)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                        quality === q.value
                          ? 'border-accent-blue bg-accent-blue/5 text-accent-blue'
                          : 'border-light-border hover:border-accent-blue/50 hover:bg-light-hover'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{q.label}</span>
                      </div>
                      <span className="text-xs text-light-muted">{displayCredits} 积分</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Action */}
            <button
              onClick={handleRestore}
              disabled={!selectedImage || isProcessing}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2 text-base shadow-lg disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  修复中...
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5" />
                  {isLoggedIn ? '开始修复' : '登录后修复'}
                </>
              )}
            </button>

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">使用提示</h4>
              <ul className="text-xs text-blue-700 space-y-1.5">
                <li>• 上传清晰度越高的原图，修复效果越好</li>
                <li>• 黑白照片建议选择「黑白上色」模式</li>
                <li>• 人像照片建议选择「人像精修」模式</li>
                <li>• 严重破损的照片可能需要多次尝试</li>
                <li>• 高清画质会消耗更多积分</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </AppShell>
  );
}
